import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, Ruler } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  midParentalHeight,
  khamisRocheEstimate,
  heightPercentile,
  cmToFeetInches,
  feetInchesToCm,
  formatFeetInches,
  type Gender,
} from "@/lib/calculators/height";
import { POPULATION_STATS, type Population } from "@/data/bdHeightStats";

type Bilingual = { en: string; bn: string };
type Tab = "predict" | "percentile" | "midparental" | "convert";

const STR = {
  h1: { en: "Height Calculator", bn: "হাইট ক্যালকুলেটর" },
  desc: {
    en: "Predict a child's adult height, find your height percentile in Bangladesh, or convert between cm and feet/inches.",
    bn: "একটি শিশুর প্রাপ্তবয়স্ক উচ্চতা অনুমান করুন, বাংলাদেশে আপনার হাইট পার্সেন্টাইল দেখুন, অথবা সেমি ও ফিট/ইঞ্চির মধ্যে রূপান্তর করুন।",
  },
  tPredict: { en: "Predict adult height", bn: "প্রাপ্তবয়স্ক উচ্চতা" },
  tPercentile: { en: "Height percentile", bn: "হাইট পার্সেন্টাইল" },
  tMidParental: { en: "Mid-parental", bn: "মিড-প্যারেন্টাল" },
  tConvert: { en: "Unit converter", bn: "একক রূপান্তর" },
  gender: { en: "Gender", bn: "লিঙ্গ" },
  male: { en: "Male", bn: "পুরুষ" },
  female: { en: "Female", bn: "মহিলা" },
  boy: { en: "Boy", bn: "ছেলে" },
  girl: { en: "Girl", bn: "মেয়ে" },
  father: { en: "Father's height (cm)", bn: "বাবার উচ্চতা (সেমি)" },
  mother: { en: "Mother's height (cm)", bn: "মায়ের উচ্চতা (সেমি)" },
  method: { en: "Method", bn: "পদ্ধতি" },
  midParentalLabel: { en: "Mid-parental (Tanner)", bn: "মিড-প্যারেন্টাল (ট্যানার)" },
  khamisLabel: { en: "Khamis-Roche (uses current height)", bn: "Khamis-Roche (বর্তমান উচ্চতা ব্যবহার করে)" },
  childAge: { en: "Child's age", bn: "শিশুর বয়স" },
  years: { en: "years", bn: "বছর" },
  childHeight: { en: "Child's current height (cm)", bn: "শিশুর বর্তমান উচ্চতা (সেমি)" },
  childWeight: { en: "Child's current weight (kg, optional)", bn: "শিশুর বর্তমান ওজন (কেজি, ঐচ্ছিক)" },
  predicted: { en: "Predicted adult height", bn: "প্রাপ্তবয়স্ক উচ্চতার পূর্বাভাস" },
  range: { en: "Likely range", bn: "সম্ভাব্য পরিসর" },
  yourHeight: { en: "Your height (cm)", bn: "আপনার উচ্চতা (সেমি)" },
  population: { en: "Population", bn: "জনগোষ্ঠী" },
  isAdult: { en: "Adult", bn: "প্রাপ্তবয়স্ক" },
  ageInput: { en: "Age (years, for context)", bn: "বয়স (বছর, প্রসঙ্গের জন্য)" },
  percentileLabel: { en: "Height percentile", bn: "হাইট পার্সেন্টাইল" },
  tallerThan: { en: "Taller than approximately", bn: "প্রায় এর চেয়ে লম্বা" },
  ofAdults: { en: "of adults in this group", bn: "এই গ্রুপের প্রাপ্তবয়স্কদের" },
  cm: { en: "cm", bn: "সেমি" },
  ft: { en: "ft", bn: "ফিট" },
  inches: { en: "inches", bn: "ইঞ্চি" },
  predictDisclaimer: {
    en: "This is an estimate, not a guarantee. Actual adult height depends on nutrition, sleep, health, hormones and many factors during growth. For concerns about a child's growth, consult a paediatrician — never use supplements or 'height-increase' products.",
    bn: "এটি একটি অনুমান, কোনো নিশ্চয়তা নয়। প্রকৃত প্রাপ্তবয়স্ক উচ্চতা পুষ্টি, ঘুম, স্বাস্থ্য, হরমোন ও আরও অনেক বিষয়ের উপর নির্ভর করে। শিশুর বৃদ্ধি নিয়ে চিন্তা থাকলে শিশু বিশেষজ্ঞের পরামর্শ নিন — কখনো সাপ্লিমেন্ট বা 'হাইট-ইনক্রিজ' পণ্য ব্যবহার করবেন না।",
  },
};

const POPULATION_KEYS: Population[] = ["BD", "SA", "GLOBAL"];

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How tall will my child be?", bn: "আমার সন্তান কত লম্বা হবে?" },
    a: {
      en: "The simplest estimate is the mid-parental formula: average the parents' heights, add 6.5 cm for a boy or subtract 6.5 cm for a girl. The actual height typically falls within ±8.5 cm of this estimate. For ages 4–17, the Khamis-Roche method (using the child's current height) is more accurate.",
      bn: "সহজতম অনুমান হলো মিড-প্যারেন্টাল সূত্র: বাবা-মার উচ্চতার গড় নিয়ে ছেলের জন্য ৬.৫ সেমি যোগ করুন, মেয়ের জন্য বিয়োগ করুন। আসল উচ্চতা সাধারণত এই অনুমানের ±৮.৫ সেমির মধ্যে থাকে।",
    },
  },
  {
    q: { en: "Can I increase my height as an adult?", bn: "প্রাপ্তবয়স্ক হিসেবে কি উচ্চতা বাড়ানো যায়?" },
    a: {
      en: "After the growth plates close (typically by 18–20 in girls and 20–22 in boys), height does not increase. Posture, core strength and shoes can change apparent height by 1–3 cm but the bones do not grow. Be cautious of any supplement or product claiming to increase adult height — these are not supported by evidence.",
      bn: "গ্রোথ প্লেট বন্ধ হয়ে গেলে (মেয়েদের ১৮-২০, ছেলেদের ২০-২২ বছরের মধ্যে) উচ্চতা আর বাড়ে না। ভঙ্গি, কোর শক্তি ও জুতা ১-৩ সেমি দৃশ্যমান উচ্চতা বাড়াতে পারে। 'হাইট বাড়ানোর' সাপ্লিমেন্ট থেকে সাবধান।",
    },
  },
  {
    q: { en: "When does height stop growing?", bn: "উচ্চতা কখন বাড়া বন্ধ হয়?" },
    a: {
      en: "Most girls reach their final height by 14–16, most boys by 16–18. Some boys continue gaining 1–2 cm until age 20–22.",
      bn: "অধিকাংশ মেয়ে ১৪-১৬ বছরে চূড়ান্ত উচ্চতায় পৌঁছায়, ছেলেরা ১৬-১৮ বছরে। কিছু ছেলে ২০-২২ বছর পর্যন্ত ১-২ সেমি বাড়তে পারে।",
    },
  },
  {
    q: { en: "What's the average height in Bangladesh?", bn: "বাংলাদেশীদের গড় উচ্চতা কত?" },
    a: {
      en: "Adult Bangladeshi men average about 163.8 cm (5'4.5\"); women about 151.8 cm (5'0\"). Source: Bangladesh DHS / BBS.",
      bn: "প্রাপ্তবয়স্ক বাংলাদেশী পুরুষের গড় উচ্চতা প্রায় ১৬৩.৮ সেমি (৫'৪.৫\"), নারীর ১৫১.৮ সেমি (৫'০\")। উৎস: BBS / DHS।",
    },
  },
  {
    q: { en: "How accurate is height prediction?", bn: "হাইট প্রেডিকশন কতটা নির্ভুল?" },
    a: {
      en: "Mid-parental height has a typical error of ±8.5 cm — most predictions land within that range, but not all. Khamis-Roche reduces error somewhat for older children (4-17). Treat any prediction as a starting estimate, not a fixed outcome.",
      bn: "মিড-প্যারেন্টাল হাইটের সাধারণ ত্রুটি ±৮.৫ সেমি — অধিকাংশ অনুমান এই পরিসরে পড়ে, সব না। Khamis-Roche বড় বাচ্চাদের (৪-১৭) জন্য কিছুটা নির্ভুল। এটি শুরুর অনুমান, চূড়ান্ত ফলাফল নয়।",
    },
  },
  {
    q: { en: "What factors affect a child's final height?", bn: "শিশুর চূড়ান্ত উচ্চতার নিয়ামক কী কী?" },
    a: {
      en: "Genetics accounts for about 80% of adult height. The rest depends on nutrition (especially protein and micronutrients), sleep (growth hormone), overall health, and absence of chronic illness. Nothing about ethnicity is 'fixed' — global averages have risen as nutrition has improved.",
      bn: "প্রাপ্তবয়স্ক উচ্চতার প্রায় ৮০% জেনেটিক। বাকিটা পুষ্টি (বিশেষ করে প্রোটিন ও মাইক্রোনিউট্রিয়েন্ট), ঘুম (গ্রোথ হরমোন), স্বাস্থ্য ও দীর্ঘস্থায়ী রোগের অনুপস্থিতির উপর নির্ভর করে।",
    },
  },
  {
    q: { en: "When should I see a paediatrician about my child's height?", bn: "শিশুর উচ্চতা নিয়ে কখন ডাক্তার দেখাব?" },
    a: {
      en: "If a child is consistently below the 3rd percentile, growing less than 4 cm/year between ages 4 and puberty, or has dropped significantly across percentiles over 6–12 months, consult a paediatrician. Do not self-treat with supplements or hormones.",
      bn: "একটি শিশু যদি ক্রমাগত ৩য় পার্সেন্টাইলের নিচে থাকে, ৪ বছর থেকে কৈশোর পর্যন্ত বছরে ৪ সেমির কম বাড়ে, বা ৬-১২ মাসে পার্সেন্টাইলে উল্লেখযোগ্যভাবে নামে — তাহলে শিশু বিশেষজ্ঞের পরামর্শ নিন। সাপ্লিমেন্ট বা হরমোন নিজে দেবেন না।",
    },
  },
  {
    q: { en: "আমার সন্তান কত লম্বা হবে?", bn: "আমার সন্তান কত লম্বা হবে?" },
    a: {
      en: "উপরে 'প্রাপ্তবয়স্ক উচ্চতা' ট্যাবে বাবা-মায়ের উচ্চতা দিয়ে অনুমান দেখুন। ফলাফলটি একটি পরিসর — শিশুর প্রকৃত উচ্চতা এর মধ্যে থাকার সম্ভাবনা বেশি।",
      bn: "উপরে 'প্রাপ্তবয়স্ক উচ্চতা' ট্যাবে বাবা-মায়ের উচ্চতা দিয়ে অনুমান দেখুন। ফলাফলটি একটি পরিসর — শিশুর প্রকৃত উচ্চতা এর মধ্যে থাকার সম্ভাবনা বেশি।",
    },
  },
];

export const Route = createFileRoute("/height-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Height Calculator — Predict Adult Height",
      description:
        "Free height calculator. Predict child's adult height (mid-parental, Khamis-Roche). Height percentile for Bangladesh. হাইট ক্যালকুলেটর।",
      path: "/height-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Height Calculator", path: "/height-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: HeightPage,
});

function HeightPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number, d = 1) => num(n.toFixed(d));
  const fmtFt = (cm: number) => {
    const s = formatFeetInches(cm);
    return lang === "bn" ? toBengaliNumerals(s) : s;
  };

  const [tab, setTab] = useState<Tab>("predict");

  return (
    <CalculatorLayout
      slug="height-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Ruler"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Tabs */}
      <div className="flex flex-wrap gap-1 rounded-xl border border-border bg-card p-1">
        {([
          ["predict", tr("tPredict")],
          ["percentile", tr("tPercentile")],
          ["midparental", tr("tMidParental")],
          ["convert", tr("tConvert")],
        ] as [Tab, string][]).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            className={cn(
              "flex-1 min-w-[120px] rounded-lg px-3 py-2 text-xs font-medium transition-colors",
              tab === k ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
              lang === "bn" && "font-bn",
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "predict" && <PredictTab lang={lang} num={num} fmt={fmt} fmtFt={fmtFt} tr={tr} />}
        {tab === "percentile" && <PercentileTab lang={lang} num={num} fmt={fmt} fmtFt={fmtFt} tr={tr} />}
        {tab === "midparental" && <MidParentalTab lang={lang} num={num} fmt={fmt} fmtFt={fmtFt} tr={tr} />}
        {tab === "convert" && <ConvertTab lang={lang} num={num} fmt={fmt} />}
      </div>

      {/* Disclaimer (only on predictive tabs) */}
      {(tab === "predict" || tab === "midparental") && (
        <div className={cn(
          "mt-6 flex items-start gap-3 rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-4 text-xs text-foreground/80",
          lang === "bn" && "font-bn",
        )}>
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
          <p>{tr("predictDisclaimer")}</p>
        </div>
      )}

      {/* Cross-links */}
      <div className="mt-6 flex flex-wrap gap-3 text-xs">
        <Link to="/cm-to-feet" className="inline-flex items-center gap-1 text-accent hover:underline">CM to Feet <ArrowRight size={12} /></Link>
        <Link to="/bmi-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">BMI <ArrowRight size={12} /></Link>
        <Link to="/calorie-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">Calorie <ArrowRight size={12} /></Link>
        <Link to="/body-type-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">Body Type <ArrowRight size={12} /></Link>
      </div>

      <article className="mt-12 space-y-10">
        <Section title={lang === "bn" ? "শিশুর প্রাপ্তবয়স্ক উচ্চতা কীভাবে অনুমান করা হয়" : "How a child's adult height is predicted"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "দুটি প্রধান পদ্ধতি ব্যবহৃত হয়: (১) মিড-প্যারেন্টাল হাইট — বাবা-মার গড় উচ্চতা থেকে ছেলেদের জন্য ৬.৫ সেমি যোগ, মেয়েদের জন্য বিয়োগ। (২) Khamis-Roche — শিশুর বর্তমান উচ্চতা, বয়স ও বাবা-মার উচ্চতা একসাথে ব্যবহার করে।"
              : "Two main methods are used: (1) Mid-parental height — average the parents' heights and add 6.5 cm for boys or subtract 6.5 cm for girls. (2) Khamis-Roche — combines the child's current height, age and parent heights for a more accurate estimate from age 4 onward."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "উচ্চতার নিয়ামক" : "Factors affecting height"} lang={lang}>
          <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            <li>{lang === "bn" ? "জেনেটিক্স — প্রায় ৮০%" : "Genetics — about 80%"}</li>
            <li>{lang === "bn" ? "পুষ্টি (প্রোটিন, ক্যালসিয়াম, ভিটামিন D, জিঙ্ক)" : "Nutrition (protein, calcium, vitamin D, zinc)"}</li>
            <li>{lang === "bn" ? "ঘুম — গ্রোথ হরমোন রাতে নিঃসরিত হয়" : "Sleep — growth hormone is released at night"}</li>
            <li>{lang === "bn" ? "সাধারণ স্বাস্থ্য ও দীর্ঘস্থায়ী রোগের অনুপস্থিতি" : "General health and absence of chronic illness"}</li>
            <li>{lang === "bn" ? "শৈশবে শারীরিক কার্যকলাপ" : "Physical activity in childhood"}</li>
          </ul>
        </Section>

        <Section title={lang === "bn" ? "বাংলাদেশীদের গড় উচ্চতা" : "Average height in Bangladesh"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "প্রাপ্তবয়স্ক পুরুষ: প্রায় ১৬৩.৮ সেমি (৫'৪.৫\")। প্রাপ্তবয়স্ক নারী: প্রায় ১৫১.৮ সেমি (৫'০\")। বৈশ্বিক গড়: পুরুষ ১৭১ সেমি, নারী ১৫৯.৫ সেমি (NCD-RisC, ২০১৬)। গত ১০০ বছরে বিশ্বব্যাপী গড় উচ্চতা ১০-১৫ সেমি বেড়েছে — মূলত উন্নত পুষ্টির কারণে।"
              : "Adult men: ~163.8 cm (5'4.5\"). Adult women: ~151.8 cm (5'0\"). Global averages: men ~171 cm, women ~159.5 cm (NCD-RisC 2016). Average heights worldwide have risen 10–15 cm over the past century, driven mainly by improved nutrition."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "শিশুর বৃদ্ধি ট্র্যাক করা" : "Tracking childhood growth"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "প্রতি ৬-১২ মাসে শিশুর উচ্চতা মেপে গ্রোথ চার্টে চিহ্নিত করুন। যদি শিশু ক্রমাগত ৩য় পার্সেন্টাইলের নিচে থাকে, বছরে ৪ সেমির কম বাড়ে, বা পার্সেন্টাইল লাইন ক্রস করে নামে — শিশু বিশেষজ্ঞের পরামর্শ নিন।"
              : "Measure your child every 6–12 months and plot on a growth chart. If they consistently sit below the 3rd percentile, gain less than 4 cm/year, or cross down across percentile lines, see a paediatrician."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "দাবিত্যাগ" : "Disclaimer"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("predictDisclaimer")}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

// ---------------------------------------------------------------------------
type CommonProps = {
  lang: "en" | "bn";
  num: (n: number | string) => string;
  fmt: (n: number, d?: number) => string;
  fmtFt: (cm: number) => string;
  tr: (k: keyof typeof STR) => string;
};

function PredictTab({ lang, num, fmt, fmtFt, tr }: CommonProps) {
  const [method, setMethod] = useState<"mp" | "kr">("mp");
  const [gender, setGender] = useState<Gender>("male");
  const [father, setFather] = useState("175");
  const [mother, setMother] = useState("162");
  const [age, setAge] = useState("8");
  const [childH, setChildH] = useState("130");
  const [childW, setChildW] = useState("28");

  const result = useMemo(() => {
    const f = parseFloat(father), m = parseFloat(mother);
    if (!isFinite(f) || !isFinite(m) || f < 100 || f > 230 || m < 100 || m > 230) return null;
    if (method === "mp") return midParentalHeight({ gender, fatherCm: f, motherCm: m });
    const a = parseFloat(age), ch = parseFloat(childH);
    if (!isFinite(a) || a < 2 || a > 18 || !isFinite(ch) || ch < 50 || ch > 200) return null;
    return khamisRocheEstimate({ gender, fatherCm: f, motherCm: m, ageYears: a, childHeightCm: ch, childWeightKg: parseFloat(childW) || undefined });
  }, [method, gender, father, mother, age, childH, childW]);

  const shareLine = result ? `${tr("predicted")}: ${fmt(result.predictedCm)} cm (${fmtFt(result.predictedCm)})` : "";

  return (
    <div className="space-y-5">
      <Toggle
        lang={lang}
        label={tr("method")}
        options={[
          { k: "mp", label: tr("midParentalLabel") },
          { k: "kr", label: tr("khamisLabel") },
        ]}
        value={method}
        onChange={(v) => setMethod(v as "mp" | "kr")}
      />

      <Toggle
        lang={lang}
        label={tr("gender")}
        options={[
          { k: "male", label: tr("boy") },
          { k: "female", label: tr("girl") },
        ]}
        value={gender}
        onChange={(v) => setGender(v as Gender)}
      />

      <div className="grid gap-5 md:grid-cols-2">
        <NumberInput label={tr("father")} value={father} onChange={setFather} unit="cm" />
        <NumberInput label={tr("mother")} value={mother} onChange={setMother} unit="cm" />
        {method === "kr" && (
          <>
            <NumberInput label={tr("childAge")} value={age} onChange={setAge} unit={tr("years")} />
            <NumberInput label={tr("childHeight")} value={childH} onChange={setChildH} unit="cm" />
            <NumberInput label={tr("childWeight")} value={childW} onChange={setChildW} unit="kg" />
          </>
        )}
      </div>

      {result && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-accent/30 bg-accent/5 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>{tr("predicted")}</p>
              <p className="mt-1 flex items-center gap-2 text-3xl font-semibold text-foreground md:text-4xl">
                <Ruler size={26} className="text-accent" />
                <span className="tabular-nums">{fmt(result.predictedCm)} {tr("cm")}</span>
              </p>
              <p className={cn("mt-1 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                ≈ <span className="tabular-nums text-foreground">{fmtFt(result.predictedCm)}</span>
              </p>
              <p className={cn("mt-3 text-sm", lang === "bn" && "font-bn")}>
                <span className="text-muted-foreground">{tr("range")}:</span>{" "}
                <span className="tabular-nums font-medium text-foreground">
                  {fmt(result.lowCm)}–{fmt(result.highCm)} {tr("cm")}
                </span>{" "}
                <span className="text-muted-foreground tabular-nums">({fmtFt(result.lowCm)} – {fmtFt(result.highCm)})</span>
              </p>
            </div>
            <CopyResultButton text={shareLine} />
          </div>
          <div className="mt-3 flex justify-end">
            <ShareButton title={shareLine} />
          </div>
        </motion.div>
      )}
    </div>
  );
}

function MidParentalTab(props: CommonProps) {
  return <PredictTab {...props} key="mp-only" />;
}

function PercentileTab({ lang, num, fmt, fmtFt, tr }: CommonProps) {
  const [gender, setGender] = useState<Gender>("male");
  const [population, setPopulation] = useState<Population>("BD");
  const [height, setHeight] = useState("170");

  const result = useMemo(() => {
    const h = parseFloat(height);
    if (!isFinite(h) || h < 80 || h > 230) return null;
    return heightPercentile({ gender, heightCm: h, population });
  }, [gender, population, height]);

  const shareLine = result ? `${tr("yourHeight")}: ${fmt(parseFloat(height))} cm — ${fmt(result.percentile, 0)}th percentile (${POPULATION_STATS[population].label.en})` : "";

  return (
    <div className="space-y-5">
      <Toggle
        lang={lang}
        label={tr("gender")}
        options={[{ k: "male", label: tr("male") }, { k: "female", label: tr("female") }]}
        value={gender}
        onChange={(v) => setGender(v as Gender)}
      />

      <div className="space-y-1.5">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("population")}</label>
        <div className="inline-flex flex-wrap rounded-lg border border-border bg-muted p-0.5">
          {POPULATION_KEYS.map((p) => {
            const active = p === population;
            return (
              <button
                key={p}
                type="button"
                onClick={() => setPopulation(p)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition",
                  active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                  lang === "bn" && "font-bn",
                )}
              >
                {POPULATION_STATS[p].label[lang]}
              </button>
            );
          })}
        </div>
      </div>

      <NumberInput label={tr("yourHeight")} value={height} onChange={setHeight} unit="cm" />

      {result && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-xl border border-accent/30 bg-accent/5 p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>{tr("percentileLabel")}</p>
              <p className="mt-1 text-3xl font-semibold text-foreground md:text-4xl tabular-nums">
                {fmt(result.percentile, 0)}<span className="text-lg text-muted-foreground">th</span>
              </p>
              <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                {tr("tallerThan")} <span className="font-semibold tabular-nums text-foreground">{fmt(result.percentile, 0)}%</span> {tr("ofAdults")} ({POPULATION_STATS[population].label[lang]}).
              </p>
              <p className={cn("mt-2 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                {lang === "bn" ? "গ্রুপ গড়" : "Group mean"}: <span className="tabular-nums text-foreground">{fmt(result.meanCm)} cm</span> ·{" "}
                Z = <span className="tabular-nums text-foreground">{fmt(result.zScore, 2)}</span>
              </p>
            </div>
            <CopyResultButton text={shareLine} />
          </div>

          <BellCurve percentile={result.percentile} z={result.zScore} mean={result.meanCm} sd={result.sdCm} />
        </motion.div>
      )}
    </div>
  );
}

function ConvertTab({ lang, num, fmt }: { lang: "en" | "bn"; num: (n: number | string) => string; fmt: (n: number, d?: number) => string }) {
  const [cm, setCm] = useState("170");
  const [ft, setFt] = useState("");
  const [inches, setInches] = useState("");

  // Derived
  const cmVal = parseFloat(cm);
  const ftVal = parseFloat(ft) || 0;
  const inVal = parseFloat(inches) || 0;
  const fromCm = isFinite(cmVal) && cmVal > 0 ? cmToFeetInches(cmVal) : null;
  const fromFt = ft || inches ? feetInchesToCm(ftVal, inVal) : null;

  return (
    <div className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <NumberInput label={lang === "bn" ? "সেন্টিমিটার" : "Centimetres"} value={cm} onChange={setCm} unit="cm" />
        <div className="grid grid-cols-2 gap-3">
          <NumberInput label={lang === "bn" ? "ফিট" : "Feet"} value={ft} onChange={setFt} unit="ft" placeholder="—" />
          <NumberInput label={lang === "bn" ? "ইঞ্চি" : "Inches"} value={inches} onChange={setInches} unit="in" placeholder="—" />
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-4">
          <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn" ? "সেমি → ফিট/ইঞ্চি" : "cm → ft/in"}
          </p>
          {fromCm && isFinite(cmVal) ? (
            <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
              {num(fromCm.ft)}'{num(fromCm.inches.toFixed(1))}"
            </p>
          ) : <p className="text-sm text-muted-foreground">—</p>}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <p className={cn("text-xs uppercase tracking-wide text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn" ? "ফিট/ইঞ্চি → সেমি" : "ft/in → cm"}
          </p>
          {fromFt !== null && fromFt > 0 ? (
            <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{fmt(fromFt)} cm</p>
          ) : <p className="text-sm text-muted-foreground">—</p>}
        </div>
      </div>

      <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
        {lang === "bn" ? "আরও বিশদ রূপান্তরের জন্য " : "For more detailed conversions, see "}
        <Link to="/cm-to-feet" className="font-medium text-accent hover:underline">CM to Feet</Link>.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Toggle<T extends string>({
  lang, label, options, value, onChange,
}: { lang: "en" | "bn"; label: string; options: { k: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="space-y-1.5">
      <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{label}</label>
      <div className="inline-flex flex-wrap rounded-lg border border-border bg-muted p-0.5">
        {options.map((o) => {
          const active = o.k === value;
          return (
            <button
              key={o.k}
              type="button"
              onClick={() => onChange(o.k)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition",
                active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                lang === "bn" && "font-bn",
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BellCurve({ percentile, z, mean, sd }: { percentile: number; z: number; mean: number; sd: number }) {
  const W = 320, H = 110, pad = 16;
  // Generate a normal curve path
  const points: string[] = [];
  const N = 80;
  const zMin = -3, zMax = 3;
  let maxY = 0;
  const ys: number[] = [];
  for (let i = 0; i <= N; i++) {
    const zi = zMin + (i / N) * (zMax - zMin);
    const y = Math.exp(-(zi * zi) / 2);
    ys.push(y);
    if (y > maxY) maxY = y;
  }
  for (let i = 0; i <= N; i++) {
    const zi = zMin + (i / N) * (zMax - zMin);
    const x = pad + ((zi - zMin) / (zMax - zMin)) * (W - 2 * pad);
    const y = H - pad - (ys[i] / maxY) * (H - 2 * pad);
    points.push(`${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  const zClamp = Math.max(zMin, Math.min(zMax, z));
  const markerX = pad + ((zClamp - zMin) / (zMax - zMin)) * (W - 2 * pad);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mt-4 h-28 w-full">
      <line x1={pad} y1={H - pad} x2={W - pad} y2={H - pad} className="stroke-border" strokeWidth={1} />
      <path d={points.join(" ")} className="fill-accent/10 stroke-accent" strokeWidth={1.5} />
      <line x1={markerX} y1={pad} x2={markerX} y2={H - pad} className="stroke-accent" strokeWidth={2} strokeDasharray="3 3" />
      <circle cx={markerX} cy={pad + 4} r={4} className="fill-accent" />
      <text x={markerX} y={pad - 2} textAnchor="middle" className="fill-accent text-[10px] font-semibold">
        {percentile.toFixed(0)}%
      </text>
    </svg>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
